import React, { useState, useMemo } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter,
    TablePagination, IconButton, Tooltip, TableSortLabel, Box
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { useTranslation } from 'react-i18next';
import { beautifyNumber } from "../../../Utils/FormatNumber";
import {
    enrichAssetsWithGroupSaleInfo,
    isAssetSold,
    formatAssetDescription,
} from "../../../Utils/assetSaleDisplayUtils";

const XTRAM_BLUE = 'var(--xtram-blue-app, #304fff)';
const XTRAM_FONT = 'var(--xtram-font)';
const BORDER = '#e5e7f6';
const SOLD_COLOR = '#c62828';

const CELL_SX = {
    py: '7px', px: '12px', fontSize: 13, fontFamily: XTRAM_FONT, whiteSpace: 'normal', wordBreak: 'break-word',
    textAlign: 'start', borderBottom: `1px solid ${BORDER}`, verticalAlign: 'middle', color: '#1f2a44',
};
const HEADER_SX = {
    ...CELL_SX, fontWeight: 800, fontSize: 12.5, whiteSpace: 'nowrap',
    backgroundColor: '#f5f6fd', color: XTRAM_BLUE, borderBottom: `2px solid ${BORDER}`,
    position: 'sticky', top: 0, zIndex: 3,
    '& .MuiTableSortLabel-root, & .MuiTableSortLabel-root:hover, & .MuiTableSortLabel-root.Mui-active': { color: XTRAM_BLUE },
    '& .MuiTableSortLabel-icon': { color: `${XTRAM_BLUE} !important` },
};
const GROUP_ROW_SX = {
    cursor: 'default',
    '& td': {
        backgroundColor: 'rgba(48, 79, 255, 0.06)', borderBottom: `1px solid ${BORDER}`,
        borderTop: `1px solid ${BORDER}`, py: '6px', fontWeight: 700, color: '#142d58',
    },
};
const ASSET_ROW_SX = {
    cursor: 'pointer', transition: 'background 0.15s ease',
    '&:hover td': { backgroundColor: 'rgba(48, 79, 255, 0.05)' },
};
const SOLD_ROW_SX = {
    ...ASSET_ROW_SX,
    '& td': { backgroundColor: 'rgba(198, 40, 40, 0.045)' },
    '&:hover td': { backgroundColor: 'rgba(198, 40, 40, 0.09)' },
};
const FOOTER_CELL_SX = {
    ...CELL_SX, fontWeight: 800, fontSize: 13.5, whiteSpace: 'nowrap', color: XTRAM_BLUE,
    backgroundColor: '#f5f6fd', borderTop: `2px solid ${BORDER}`, borderBottom: 'none',
    position: 'sticky', bottom: 0, zIndex: 2,
};
const CHIP_SX = {
    display: 'inline-flex', alignItems: 'center', height: 20, px: '8px', borderRadius: 999,
    fontSize: 11.5, fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap',
};

const COLUMNS = [
    { id: 'assetName', labelKey: 'assetsPage.columns.asset', width: '26%', type: 'string' },
    { id: 'activationDate', labelKey: 'assetsPage.columns.activationDate', width: '11%', type: 'date' },
    { id: 'originalPrice', labelKey: 'assetsPage.columns.originalPrice', width: '11%', type: 'number' },
    { id: 'depreciationPercentage', labelKey: 'assetsPage.columns.depreciationPercentage', width: '8%', type: 'number' },
    { id: 'sellDate', labelKey: 'assetsPage.columns.sellDate', width: '11%', type: 'date' },
    { id: 'sellAmount', labelKey: 'assetsPage.columns.sellAmount', width: '11%', type: 'number' },
    { id: 'description', labelKey: 'assetsPage.columns.notes', width: '22%', type: 'string' },
];

const sumBy = (rows, key) => rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
const hasValue = (v) => v != null && v !== '';

function AssetTable({
    assets,
    assetGroups,
    assetRelations = [],
    expandedRows,
    handleExpandClick,
    onRowClick,
    searchQuery,
    showEmptyGroups = false,
    t: tProp,
}) {
    const { t: tHook, i18n } = useTranslation();
    const t = tProp || tHook;
    const dir = i18n.dir();
    const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc", type: "string" });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const displayAssets = useMemo(
        () => enrichAssetsWithGroupSaleInfo(assets, assetRelations),
        [assets, assetRelations]
    );

    const groups = useMemo(() => {
        const all = groupAssetsByGroup(displayAssets, assetGroups, t);
        return showEmptyGroups ? all : all.filter((g) => g.children?.length > 0);
    }, [displayAssets, assetGroups, showEmptyGroups, t]);

    const filteredAndSortedGroups = useMemo(() => {
        const q = (searchQuery || "").toLowerCase();
        let result = groups.map((group) => {
            const filtered = group.children.filter((a) =>
                (a.assetNumber?.toString() || '').includes(q) ||
                (a.assetName?.toLowerCase() || '').includes(q) ||
                (a.description?.toLowerCase() || '').includes(q) ||
                formatDate(a.purchaseDate).toLowerCase().includes(q) ||
                formatDate(a.activationDate).toLowerCase().includes(q) ||
                (a.originalPrice != null && a.originalPrice.toString().includes(q)) ||
                (a.depreciationPercentage != null && a.depreciationPercentage.toString().includes(q))
            );
            if (group.assetGroupName?.toLowerCase().includes(q) || filtered.length > 0) {
                return { ...group, children: filtered };
            }
            return null;
        }).filter(Boolean);

        if (sortConfig.key) {
            result.forEach((g) => {
                g.children.sort((a, b) => {
                    const av = a[sortConfig.key] ?? "";
                    const bv = b[sortConfig.key] ?? "";
                    const sign = sortConfig.direction === "asc" ? 1 : -1;
                    if (sortConfig.type === "number") return sign * ((Number(av) || 0) - (Number(bv) || 0));
                    if (sortConfig.type === "date") return sign * (new Date(av || 0) - new Date(bv || 0));
                    return sign * String(av).localeCompare(String(bv));
                });
            });
        }
        return result;
    }, [groups, searchQuery, sortConfig]);

    const currentGroups = useMemo(
        () => filteredAndSortedGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredAndSortedGroups, page, rowsPerPage]
    );

    const handleSort = (key, type) => {
        setSortConfig((prev) => ({
            key,
            type,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const isExpanded = (groupId) => expandedRows[groupId] !== false;
    const soldEdge = dir === 'rtl' ? `inset -3px 0 0 ${SOLD_COLOR}` : `inset 3px 0 0 ${SOLD_COLOR}`;

    const grandTotals = useMemo(() => {
        const all = filteredAndSortedGroups.flatMap((g) => g.children);
        return {
            count: all.length,
            totalOriginal: sumBy(all, 'originalPrice'),
            totalSell: sumBy(all.filter(isAssetSold), 'sellAmount'),
        };
    }, [filteredAndSortedGroups]);

    return (
        <Box
            sx={{
                border: `1px solid ${BORDER}`, borderRadius: '12px', background: '#fff', overflow: 'hidden', isolation: 'isolate',
                boxShadow: '0 4px 16px rgba(20, 53, 98, 0.05)', fontFamily: XTRAM_FONT,
            }}
        >
            <TableContainer
                dir={dir}
                sx={{
                    maxHeight: 'max(360px, calc(100vh - 360px))', overflow: 'auto',
                    scrollbarWidth: 'thin', scrollbarColor: 'var(--xtram-scrollbar-color, #c5cbe8) transparent',
                }}
            >
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: 860, borderCollapse: 'separate', borderSpacing: 0 }}>
                    <colgroup>
                        {COLUMNS.map((col) => <col key={col.id} style={{ width: col.width }} />)}
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            {COLUMNS.map((col) => (
                                <TableCell key={col.id} sx={HEADER_SX}>
                                    <TableSortLabel
                                        active={sortConfig.key === col.id}
                                        direction={sortConfig.direction === "asc" ? "asc" : "desc"}
                                        onClick={() => handleSort(col.id, col.type)}
                                    >
                                        {t(col.labelKey)}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {currentGroups.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={COLUMNS.length} sx={{ ...CELL_SX, textAlign: 'center', py: 6, color: '#7a8299' }}>
                                    <Inventory2OutlinedIcon sx={{ fontSize: 34, color: '#c5cbe8', display: 'block', mx: 'auto', mb: 1 }} />
                                    {t('assetsPage.empty')}
                                </TableCell>
                            </TableRow>
                        )}

                        {currentGroups.map((group) => {
                            const expanded = isExpanded(group.id);
                            const soldChildren = group.children.filter(isAssetSold);
                            const groupLabel = group.id === "NoGroup"
                                ? t('assetsPage.noGroupItems')
                                : group.assetGroupName;

                            return (
                                <React.Fragment key={group.id}>
                                    <Tooltip title={t('DoubleClickToEdit')} placement="top" enterDelay={600}>
                                        <TableRow
                                            sx={GROUP_ROW_SX}
                                            onDoubleClick={() => onRowClick(group.id, 'assetGroup')}
                                        >
                                            <TableCell sx={CELL_SX} colSpan={2}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                                    <IconButton
                                                        size="small"
                                                        disabled={group.children.length === 0}
                                                        onClick={(e) => { e.stopPropagation(); handleExpandClick(group.id); }}
                                                        sx={{ p: '2px', color: XTRAM_BLUE, background: '#fff', border: `1px solid ${BORDER}` }}
                                                    >
                                                        {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                    </IconButton>
                                                    {group.id !== "NoGroup" && (
                                                        <Box component="span" sx={{ ...CHIP_SX, background: XTRAM_BLUE, color: '#fff' }}>
                                                            {group.id}
                                                        </Box>
                                                    )}
                                                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {groupLabel}
                                                    </Box>
                                                    <Box component="span" sx={{ ...CHIP_SX, background: '#fff', color: '#5b6478', border: `1px solid ${BORDER}` }}>
                                                        {t('assetsPage.assetsInGroup', { count: group.children.length })}
                                                    </Box>
                                                    {soldChildren.length > 0 && (
                                                        <Box component="span" sx={{ ...CHIP_SX, background: 'rgba(198,40,40,0.1)', color: SOLD_COLOR }}>
                                                            {t('assetsPage.soldInGroup', { count: soldChildren.length })}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap' }}>
                                                {group.children.length > 0 ? beautifyNumber(sumBy(group.children, 'originalPrice'), 0) : '—'}
                                            </TableCell>
                                            <TableCell sx={CELL_SX}>
                                                {group.depreciationPercentage > 0 ? `${group.depreciationPercentage}%` : '—'}
                                            </TableCell>
                                            <TableCell sx={CELL_SX} />
                                            <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap', color: soldChildren.length ? SOLD_COLOR : undefined }}>
                                                {soldChildren.length > 0 ? beautifyNumber(sumBy(soldChildren, 'sellAmount'), 0) : ''}
                                            </TableCell>
                                            <TableCell sx={CELL_SX} />
                                        </TableRow>
                                    </Tooltip>

                                    {expanded && group.children.map((asset) => {
                                        const isSold = isAssetSold(asset);
                                        const displayDescription = formatAssetDescription(asset.description, asset.sellDate, isSold);
                                        const soldColor = isSold ? SOLD_COLOR : '#9aa1b5';
                                        return (
                                            <TableRow
                                                key={asset.id}
                                                sx={isSold ? SOLD_ROW_SX : ASSET_ROW_SX}
                                                onClick={() => onRowClick(asset.id, 'asset')}
                                            >
                                                <TableCell sx={{ ...CELL_SX, paddingInlineStart: '40px', boxShadow: isSold ? soldEdge : 'none' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Box component="span" sx={{ color: '#7a8299', fontWeight: 700, fontSize: 12, direction: 'ltr', unicodeBidi: 'isolate' }}>
                                                            {asset.assetNumber || '—'}
                                                        </Box>
                                                        <Box component="span" sx={{ fontWeight: 600 }}>{asset.assetName || '—'}</Box>
                                                        {isSold && (
                                                            <Box component="span" sx={{ ...CHIP_SX, height: 18, fontSize: 11, background: 'rgba(198,40,40,0.1)', color: SOLD_COLOR }}>
                                                                {t('assetsPage.soldBadge')}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap' }}>{formatDateHe(asset.activationDate)}</TableCell>
                                                <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap', fontWeight: 700, color: XTRAM_BLUE }}>
                                                    {beautifyNumber(asset.originalPrice, 0)}
                                                </TableCell>
                                                <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap' }}>
                                                    {asset.depreciationPercentage != null ? `${beautifyNumber(asset.depreciationPercentage, 0)}%` : '—'}
                                                </TableCell>
                                                <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap', color: soldColor }}>
                                                    {asset.sellDate ? formatDateHe(asset.sellDate) : '—'}
                                                </TableCell>
                                                <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap', color: soldColor, fontWeight: isSold ? 700 : 400 }}>
                                                    {hasValue(asset.sellAmount) ? beautifyNumber(asset.sellAmount, 0) : '—'}
                                                </TableCell>
                                                <TableCell sx={{ ...CELL_SX, fontSize: 12, color: isSold ? SOLD_COLOR : '#5b6478' }}>
                                                    {displayDescription || ''}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                    {currentGroups.length > 0 && (
                        <TableFooter>
                            <TableRow>
                                <TableCell sx={FOOTER_CELL_SX} colSpan={2}>
                                    {t('GrandTotal')}
                                    <Box component="span" sx={{ ...CHIP_SX, marginInlineStart: '8px', background: '#fff', color: '#5b6478', border: `1px solid ${BORDER}` }}>
                                        {t('assetsPage.assetsInGroup', { count: grandTotals.count })}
                                    </Box>
                                </TableCell>
                                <TableCell sx={FOOTER_CELL_SX}>{beautifyNumber(grandTotals.totalOriginal, 0)}</TableCell>
                                <TableCell sx={FOOTER_CELL_SX} />
                                <TableCell sx={FOOTER_CELL_SX} />
                                <TableCell sx={{ ...FOOTER_CELL_SX, color: grandTotals.totalSell ? SOLD_COLOR : undefined }}>
                                    {grandTotals.totalSell ? beautifyNumber(grandTotals.totalSell, 0) : ''}
                                </TableCell>
                                <TableCell sx={FOOTER_CELL_SX} />
                            </TableRow>
                        </TableFooter>
                    )}
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={filteredAndSortedGroups.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                labelRowsPerPage={t("RowsPerPage")}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("of")} ${count}`}
                sx={{
                    direction: dir,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    borderTop: `1px solid ${BORDER}`,
                    background: '#fff',
                    overflow: 'visible',
                    fontFamily: XTRAM_FONT,
                    color: 'var(--xtram-text)',
                    '& .MuiTablePagination-spacer': {
                        display: 'none',
                    },
                    '& .MuiTablePagination-toolbar': {
                        minHeight: 40,
                        paddingInline: 8,
                        flexWrap: 'wrap',
                        justifyContent: 'flex-start',
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        margin: 0,
                        fontSize: 13,
                        fontFamily: XTRAM_FONT,
                        color: 'var(--xtram-text)',
                    },
                    '& .MuiTablePagination-displayedRows': {
                        direction: 'ltr',
                        unicodeBidi: 'isolate',
                    },
                    '& .MuiTablePagination-select': {
                        fontFamily: XTRAM_FONT,
                        fontSize: 13,
                    },
                    '& .MuiTablePagination-actions': {
                        marginInlineStart: 8,
                        flexDirection: dir === 'rtl' ? 'row-reverse' : 'row',
                    },
                    '& .MuiIconButton-root': {
                        color: XTRAM_BLUE,
                        transform: dir === 'rtl' ? 'scaleX(-1)' : 'none',
                    },
                }}
            />
        </Box>
    );
}

function groupAssetsByGroup(assets, assetGroups, t) {
    const map = {};
    (assetGroups || []).forEach((g) => {
        map[g.assetGroupId] = {
            id: g.assetGroupId,
            assetGroupName: g.name,
            depreciationPercentage: g.depreciationPercentage ?? 0,
            children: [],
        };
    });
    map["NoGroup"] = { id: "NoGroup", assetGroupName: t?.('NoGroup') || "ללא קבוצה", depreciationPercentage: 0, children: [] };

    (assets || []).forEach((a) => {
        const gid = a.assetGroupId && map[a.assetGroupId] ? a.assetGroupId : "NoGroup";
        map[gid].children.push({
            id: a.id,
            assetNumber: a.assetNumber,
            assetName: a.name,
            purchaseDate: a.purchaseDate,
            activationDate: a.activationDate || a.purchaseDate,
            originalPrice: a.originalPrice,
            depreciationPercentage: a.depreciationPercentage,
            description: a.description,
            sellDate: a.sellDate,
            sellAmount: a.sellAmount,
            saleIncomeId: a.saleIncomeId,
            saleJournalEntryId: a.saleJournalEntryId,
            soldViaAssetGroup: a.soldViaAssetGroup,
        });
    });
    return Object.values(map);
}

const formatDate = (d) => (!d ? "-" : new Date(d).toLocaleDateString());
const formatDateHe = (d) =>
    !d ? "—" : new Date(d).toLocaleDateString("he-IL");

export default AssetTable;
